import React from 'react';
import { Box, Typography, Divider } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { BrandLogo } from '../BrandLogo';
import { useThemeContext } from '../../context/ThemeContext';

export const Footer = () => {
    const navigate = useNavigate();
    const { mode } = useThemeContext();
    const isLight = mode === 'light';

    const footerLinks = {
        Product: [
            { name: 'Catalog', id: 'catalog-section' },
            { name: '1:1 Mentorship', path: '/interview-prep/mentorship-inquiry' }
        ],
        Support: [
            { name: 'Give Feedback', path: '/interview-prep/feedback', icon: '💡' },
            { name: 'WhatsApp Us', href: 'https://wa.me/918340156311', icon: '💬' },
            { name: 'Email Core', href: 'mailto:core@edgent.in', icon: '✉️' }
        ],
        Legal: [
            { name: 'Privacy Policy', path: '/interview-prep/privacy' },
            { name: 'Terms of Service', path: '/interview-prep/terms' }
        ]
    };

    return (
        <Box sx={{ bgcolor: isLight ? '#ffffff' : '#000000', borderTop: isLight ? '1px solid #e2e8f0' : '1px solid #1f1f22', pt: { xs: 8, xl: 12 }, pb: 4, mt: 8, zIndex: 1, position: 'relative' }}>
            <Box sx={{ maxWidth: { xs: 1100, xl: 1400 }, mx: 'auto', px: 4, display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', mb: 6, gap: { xs: 4, xl: 12 } }}>
                <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <BrandLogo size="small" isLight={isLight} />
                    </Box>
                    <Typography variant="body2" sx={{ color: isLight ? '#64748b' : '#a1a1aa', maxWidth: 350, fontSize: { xl: '1rem' } }}>
                        The interactive learning platform for engineers who want to deeply understand systems, not just pass interviews.
                    </Typography>
                </Box>

                <Box sx={{ display: 'flex', gap: { xs: 4, md: 8, xl: 12 }, flexWrap: 'wrap' }}>
                    {Object.entries(footerLinks).map(([title, links]) => (
                        <Box key={title}>
                            <Typography variant="subtitle2" fontWeight="700" sx={{ mb: 2, color: isLight ? '#1e293b' : '#e2e8f0', fontSize: { xl: '1.1rem' } }}>{title}</Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                {links.map(link => (
                                    <Typography
                                        key={link.name}
                                        variant="body2"
                                        onClick={() => {
                                            if (link.id) {
                                                document.getElementById(link.id)?.scrollIntoView({ behavior: 'smooth' });
                                            } else if (link.path) {
                                                navigate(link.path);
                                            }
                                        }}
                                        component={link.href ? 'a' : 'p'}
                                        href={link.href}
                                        target={link.href ? "_blank" : undefined}
                                        rel={link.href ? "noopener noreferrer" : undefined}
                                        sx={{
                                            cursor: 'pointer',
                                            textDecoration: 'none',
                                            color: isLight ? '#64748b' : '#a1a1aa',
                                            display: 'flex',
                                            alignItems: 'center',
                                            fontSize: { xl: '0.95rem' },
                                            gap: 1,
                                            '&:hover': { color: '#3b82f6' }
                                        }}
                                    >
                                        {link.icon} {link.name}
                                    </Typography>
                                ))}
                            </Box>
                        </Box>
                    ))}
                </Box>
            </Box>

            {/* Trust Badges */}
            <Box sx={{
                maxWidth: { xs: 1100, xl: 1400 }, mx: 'auto', px: 4, display: 'flex', flexWrap: 'wrap',
                justifyContent: 'center', gap: { xs: 4, md: 8, xl: 16 }, mb: 4, opacity: 0.8
            }}>
                {[
                    { icon: '🔒', text: 'Secure Payment' },
                    { icon: '⚡', text: 'Lifetime Access' },
                    { icon: '🎓', text: 'Professional Grade' }
                ].map((badge, idx) => (
                    <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography sx={{ fontSize: '1.2rem' }}>{badge.icon}</Typography>
                        <Typography variant="caption" fontWeight="700" sx={{
                            color: isLight ? '#475569' : '#94a3b8',
                            letterSpacing: '0.5px', textTransform: 'uppercase'
                        }}>
                            {badge.text}
                        </Typography>
                    </Box>
                ))}
            </Box>

            <Divider sx={{ mb: 4, borderColor: isLight ? '#f1f5f9' : '#141417' }} />

            <Typography variant="caption" textAlign="center" display="block" sx={{ color: isLight ? '#94a3b8' : '#52525b' }}>
                © {new Date().getFullYear()} FlowState. Built for visual thinkers.
            </Typography>
        </Box>
    );
};
